<template>
  <v-dialog
    content-class="add-organization-popup"
    :value="internalValue"
    :width="width"
    @click:outside="onEscapeClicked"
    @keydown.esc="onEscapeClicked"
  >
    <div class='add-organization-popup__action'>
      <div class='add-organization-popup__action__title'>
        {{ $t('add.organization.popup.title') }}
      </div>
      <span class='close_btn' @click='onEscapeClicked'>
        <span class="material-icons-outlined">
          close
        </span>
      </span>
    </div>
    <div class='add-organization-popup__details'>
      <avatar-upload
        class="avatar"
        :file-url="fileUrl"
        @changed='updateAvatarUrl'
        for-organization
        :init-animation='internalValue'
      />
      <div class='add-organization-popup__details__form'>
        <span v-if='!valid' class='add-organization-popup__alert'>
          <v-alert
            text
            dense
            color="#e0255e"
            icon="info"
            border="left"
            class='not-valid-message'
          >
            {{ $t('organization.not.valid.message') }}
          </v-alert>
        </span>
        <edit-company-customer-profile-form
          :value='organization'
          class="profile-form"
          :valid='valid'
          :new-organization='!editMode'
          @updateValid='updateValid'
        />
        <v-btn class='submit' color="primary" @click="submit" :loading='loading' :disabled='!valid'>
          {{ $t('save') }}
        </v-btn>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import AvatarUpload from '@/components/common/AvatarUpload.vue';
import EditCompanyCustomerProfileForm from '@/components/forms/EditCompanyCustomerProfileForm.vue';
import { ADD_OR_UPDATE_ORGANIZATION } from '@/store/organization/actionTypes';
import disableOuterScrollOnPopupVisible from '@/mixins/disableOuterScrollOnPopupVisible';

export default {
  name: 'AddOrganizationPopup',
  components: { EditCompanyCustomerProfileForm, AvatarUpload },
  mixins: [disableOuterScrollOnPopupVisible],
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    editMode: {
      type: Boolean,
      default: false,
    },
    organization: {
      required: false,
      default: () => {},
      type: Object,
    },
  },
  data() {
    return {
      loading: false,
      valid: false,
      avatarUrlUpdated: false,
    };
  },
  methods: {
    updateValid(valid) {
      this.valid = valid;
    },
    onEscapeClicked() {
      this.$emit('closed');
    },
    updateAvatarUrl(file) {
      this.$set(this.organization, 'logoURL', file);
      this.avatarUrlUpdated = true;
    },
    async submit() {
      this.loading = true;
      const payload = { ...this.organization };
      if (!this.avatarUrlUpdated) {
        delete payload.logoURL;
      }
      try {
        await this.$store.dispatch(ADD_OR_UPDATE_ORGANIZATION, payload);
      } catch (e) {
        if (e.message === 'User is unauthorized to perform this action') {
          this.$emit('changeErrorBanner', e.message);
        } else {
          this.$emit('updateErrorMessage', e.message);
        }
      }
      this.loading = false;
      this.avatarUrlUpdated = false;
      this.$emit('closed');
    },
  },
  computed: {
    width() {
      return 650;
    },
    fileUrl() {
      if (!this.organization?.logoURL || this.organization?.logoURL?.startsWith('data')) {
        return this.organization?.logoURL;
      }
      return `${this.organization?.logoURL}?${Date.now().toString()}` || null;
    },
    internalValue: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
  },
};
</script>

<style lang='scss'>
@import '~@/assets/styles/design-system';

.add-organization-popup {
  .v-alert__icon.v-icon {
    color: var(--severity-critical);
  }
}
</style>

<style lang='scss' scoped>
@import '~@/assets/styles/design-system';

.not-valid-message {
  font-size: $font-size-xs;
  font-weight: 600;
  letter-spacing: 1.12px;
  line-height: $space-4;
}

.add-organization-popup {
  &__action {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: $space-6;

    .close_btn {
      float: right;

      &:hover {
        cursor: pointer;
        color: var(--brand-primary);
      }
    }

    &__title {
      color: var(--text-primary);
      font-family: $default-application-font-bold;
      font-size: $font-size-xl;
      font-weight: bold;
      line-height: $font-size-xl;
      letter-spacing: 0;
    }
  }

  &__details {
    display: flex;
    flex-direction: column;
    padding: $space-2 $space-9 $space-9 $space-9;

    &__form {
      .submit {
        margin-top: 2%;
        float: right;
      }

      &__alert {
        margin-top: 2%;
      }
    }
  }
}
</style>
